import React, { useState, useEffect, useRef } from 'react';
import { eventsAPI, weatherAPI } from '../../services/api';
import logger from '../../utils/logger';
import styles from './Maps.module.css';

const Maps = () => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [weatherData, setWeatherData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mapType, setMapType] = useState('roadmap');
  const [showWeatherLayer, setShowWeatherLayer] = useState(true);

  // München center coordinates - Phase 6: Updated from Istanbul to München
  const muenchenCenter = { lat: 48.1351, lng: 11.5820 };

  useEffect(() => {
    // Load Google Maps script (for demo, we'll use a mock implementation)
    initializeMap();
    loadEventsData();
  }, []);

  const initializeMap = () => {
    // Mock Google Maps implementation for demo
    logger.log('Initializing Google Maps...');
    setMap({ 
      center: muenchenCenter, 
      zoom: 12,
      markers: []
    });
  };

  const loadEventsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load events
      const eventsResponse = await eventsAPI.getUpcoming();
      if (eventsResponse.success) {
        const eventsData = eventsResponse.data.events || [];
        setEvents(eventsData);

        // Load weather for each event location
        const locations = eventsData.map(event => ({
          lat: parseFloat(event.latitude),
          lon: parseFloat(event.longitude),
          eventId: event.id,
          eventName: event.name
        }));

        if (locations.length > 0) {
          const weatherResponse = await weatherAPI.getWeatherForEvents(locations);
          if (weatherResponse.success) {
            const weatherMap = {};
            weatherResponse.data.weatherData.forEach(item => {
              weatherMap[item.eventId] = item;
            });
            setWeatherData(weatherMap);
          }
        }
      }
    } catch (err) {
      logger.error('Events/Weather loading error:', err);
      setError('Karten-Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    // In real implementation, this would center the map on the event
    logger.log('Centering map on event:', event.name);
  };

  const getMarkerColor = (demandLevel) => {
    const colors = {
      'SEHR_HOCH': '#ff4444',
      'HOCH': '#ff8800',
      'MEDIUM': '#ffcc00',
      'NIEDRIG': '#4CAF50'
    };
    return colors[demandLevel] || '#666';
  };

  const getWeatherIcon = (weatherMain) => {
    const icons = {
      'Clear': '☀️',
      'Rain': '🌧️',
      'Clouds': '☁️',
      'Snow': '❄️',
      'Thunderstorm': '⛈️',
      'Drizzle': '🌦️',
      'Mist': '🌫️',
      'Fog': '🌫️'
    };
    return icons[weatherMain] || '🌤️';
  };

  const formatDistance = (lat1, lon1, lat2, lon2) => {
    // Simple distance calculation for demo
    const distance = Math.sqrt(Math.pow(lat2 - lat1, 2) + Math.pow(lon2 - lon1, 2)) * 111; // Approximate km
    return distance.toFixed(1);
  };

  return (
    <div className={styles.maps}>
      <div className={styles.header}>
        <h1>🗺️ Event-Karte & Navigation</h1>
        <p>Finde die besten Events in deiner Nähe mit Taxi-Nachfrage und Wetterinfo</p>
      </div>

      {/* Map Controls */}
      <div className={styles.controls}>
        <div className={styles.controlGroup}>
          <label>Kartentyp:</label>
          <select 
            value={mapType} 
            onChange={(e) => setMapType(e.target.value)}
            className={styles.select}
          >
            <option value="roadmap">Straße</option>
            <option value="satellite">Satellit</option>
            <option value="hybrid">Hybrid</option>
            <option value="terrain">Gelände</option>
          </select>
        </div>
        
        <div className={styles.controlGroup}>
          <label>
            <input
              type="checkbox"
              checked={showWeatherLayer}
              onChange={(e) => setShowWeatherLayer(e.target.checked)}
            />
            Wetter-Layer anzeigen
          </label>
        </div>

        <button 
          onClick={loadEventsData}
          className={styles.refreshBtn}
          disabled={loading}
        >
          {loading ? '🔄 Laden...' : '🔄 Aktualisieren'}
        </button>
      </div>

      {error && (
        <div className={styles.error}>
          ⚠️ {error}
        </div>
      )}

      <div className={styles.mapContainer}>
        {/* Mock Map Display */}
        <div className={styles.mockMap}>
          <div className={styles.mapOverlay}>
            <h3>🗺️ Interactive Map</h3>
            <p>Typ: {mapType.toUpperCase()}</p>
            <p>Zentrum: München ({muenchenCenter.lat}, {muenchenCenter.lng})</p>
            <p>Events: {events.length} Marker</p>
            <p>Wetter-Layer: {showWeatherLayer ? 'AN' : 'AUS'}</p>
            
            {selectedEvent && (
              <div className={styles.mapInfo}>
                <strong>Ausgewählt:</strong> {selectedEvent.name}
                <br />
                <small>{selectedEvent.location}</small>
              </div>
            )}
          </div>

          {/* Mock Event Markers */}
          <div className={styles.markers}>
            {events.map((event, index) => (
              <div
                key={event.id}
                className={styles.marker}
                style={{
                  backgroundColor: getMarkerColor(event.demandLevel),
                  left: `${20 + (index * 15)}%`,
                  top: `${30 + (index * 10)}%`
                }}
                onClick={() => handleEventClick(event)}
                title={`${event.name} - ${event.demandLevel}`}
              >
                📍
              </div>
            ))}
          </div>
        </div>

        {/* Events Sidebar */}
        <div className={styles.sidebar}>
          <h3>📋 Events auf der Karte</h3>
          
          {loading && (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>Lade Events...</p>
            </div>
          )}

          <div className={styles.eventsList}>
            {events.map((event) => {
              const weather = weatherData[event.id];
              return (
                <div
                  key={event.id}
                  className={`${styles.eventCard} ${
                    selectedEvent?.id === event.id ? styles.selected : ''
                  }`}
                  onClick={() => handleEventClick(event)}
                >
                  <div className={styles.eventHeader}>
                    <h4>{event.name}</h4>
                    <div 
                      className={styles.demandBadge}
                      style={{ backgroundColor: getMarkerColor(event.demandLevel) }}
                    >
                      {event.demandLevel}
                    </div>
                  </div>

                  <div className={styles.eventDetails}>
                    <div className={styles.eventInfo}>
                      <span>📍 {event.location}</span>
                      <span>👥 {event.expected_visitors.toLocaleString()} Besucher</span>
                      <span>🚖 ~{event.estimatedTaxis} Taxis</span>
                      <span>⏰ {event.startTime} ({event.hoursUntil}h)</span>
                    </div>

                    {weather && showWeatherLayer && (
                      <div className={styles.weatherInfo}>
                        <div className={styles.weatherHeader}>
                          <span>{getWeatherIcon(weather.weather.main)}</span>
                          <span>{weather.weather.temp}°C</span>
                        </div>
                        <div className={styles.weatherDetails}>
                          <small>{weather.weather.description}</small>
                          <div 
                            className={styles.weatherDemand}
                            style={{ 
                              color: weather.taxiRecommendation.priority === 'HIGH' ? '#ff8800' : 
                                     weather.taxiRecommendation.priority === 'VERY_HIGH' ? '#ff4444' : '#4CAF50'
                            }}
                          >
                            🚖 {weather.taxiRecommendation.demandFactor}x
                          </div>
                        </div>
                      </div>
                    )}

                    <div className={styles.eventActions}>
                      <button 
                        className={styles.navigateBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          // In real app, this would open navigation
                          alert(`Navigation zu ${event.name} würde hier starten!`);
                        }}
                      >
                        🧭 Navigation
                      </button>
                      
                      <button 
                        className={styles.distanceBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Mock distance calculation
                          const distance = formatDistance(
                            muenchenCenter.lat, muenchenCenter.lng,
                            parseFloat(event.latitude), parseFloat(event.longitude)
                          );
                          alert(`Entfernung: ~${distance} km`);
                        }}
                      >
                        📏 Entfernung
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Map Legend */}
          <div className={styles.legend}>
            <h4>🏷️ Legende</h4>
            <div className={styles.legendItems}>
              <div className={styles.legendItem}>
                <div className={styles.legendColor} style={{ backgroundColor: '#ff4444' }}></div>
                <span>Sehr hohe Nachfrage</span>
              </div>
              <div className={styles.legendItem}>
                <div className={styles.legendColor} style={{ backgroundColor: '#ff8800' }}></div>
                <span>Hohe Nachfrage</span>
              </div>
              <div className={styles.legendItem}>
                <div className={styles.legendColor} style={{ backgroundColor: '#ffcc00' }}></div>
                <span>Mittlere Nachfrage</span>
              </div>
              <div className={styles.legendItem}>
                <div className={styles.legendColor} style={{ backgroundColor: '#4CAF50' }}></div>
                <span>Niedrige Nachfrage</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className={styles.quickActions}>
        <h3>⚡ Schnellaktionen</h3>
        <div className={styles.actionButtons}>
          <button 
            className={styles.actionBtn}
            onClick={() => {
              const highDemandEvents = events.filter(e => e.demandLevel === 'SEHR_HOCH' || e.demandLevel === 'HOCH');
              if (highDemandEvents.length > 0) {
                setSelectedEvent(highDemandEvents[0]);
                alert(`Bestes Event gefunden: ${highDemandEvents[0].name}`);
              } else {
                alert('Keine Events mit hoher Nachfrage gefunden');
              }
            }}
          >
            🎯 Beste Events finden
          </button>
          
          <button 
            className={styles.actionBtn}
            onClick={() => {
              alert('Filter für Events in deiner Nähe würde hier angewendet');
            }}
          >
            📍 Events in der Nähe
          </button>
          
          <button 
            className={styles.actionBtn}
            onClick={() => {
              const rainEvents = Object.values(weatherData).filter(w => 
                w.weather.main === 'Rain' && w.taxiRecommendation.priority === 'HIGH'
              );
              if (rainEvents.length > 0) {
                alert(`${rainEvents.length} Events mit Regen und hoher Nachfrage gefunden!`);
              } else {
                alert('Keine Regen-Events mit hoher Nachfrage');
              }
            }}
          >
            🌧️ Regen-Hotspots
          </button>
        </div>
      </div>
    </div>
  );
};

export default Maps;
